import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { FaBell } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle,
  Send,
  AlertCircle,
  ClipboardList,
  CircleAlert,
} from "lucide-react";
import axios from "axios";
import API_URL from "../config";
import { connectWebSocket, disconnectWebSocket } from "../websocket";
import { useLanguage } from '../context/LanguageContext';
import { formatDateTime24 } from '../utils/dateTimeFormat';
import { getLocalDevNotifications, isLocalDevCompanyId, markLocalDevNotificationsRead } from "../utils/localDevAuth";
import { getAuthHeaders, getStoredAccessToken } from "../utils/authSession";

const LAST_OPEN_KEY = "notif_last_open_at";

const NotificationBell = ({ user, onAllNotificationsClick }) => {
  const { t, locale } = useLanguage();
  const navigate = useNavigate();
  // API list (DB truth) and WS queue (live)
  const [apiList, setApiList] = useState([]);
  const [wsQueue, setWsQueue] = useState([]);

  // Menu + tabs
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("read"); // default to Read

  // “previous open” timestamp (frozen for first-open highlight/unread)
  const [lastOpenAt, setLastOpenAt] = useState(() => localStorage.getItem(LAST_OPEN_KEY));
  const [highlightCutoff, setHighlightCutoff] = useState(null);

  // Snapshot of API-unread signatures at the moment the menu is opened
  const [unreadApiSigsAtOpen, setUnreadApiSigsAtOpen] = useState(null); // Set<string> | null

  const menuRef = useRef(null);
  const isLocalDevSession = isLocalDevCompanyId(user?.company?.id) || isLocalDevCompanyId(localStorage.getItem("company_id"));

  // Icons
  const typeIcons = {
    task_submission: <Send className="mr-2 h-4 w-4 text-blue-600" />,
    task_approved: <CheckCircle className="mr-2 h-4 w-4 text-green-500" />,
    task_declined: <AlertCircle className="mr-2 h-4 w-4 text-red-500" />,
    task_assignment: <ClipboardList className="mr-2 h-4 w-4 text-amber-500" />,
    task_pending_approval: <Send className="mr-2 h-4 w-4 text-blue-600" />,
    deadline_reminder: <AlertCircle className="mr-2 h-4 w-4 text-amber-500" />,
    approval_delay: <CircleAlert className="mr-2 h-4 w-4 text-purple-500" />,
    default: <CircleAlert className="mr-2 h-4 w-4 text-muted" />,
  };

  // ——— Normalizers
  const normalizeFromAPI = (log) => ({
    id: log.id,
    source: "api",
    type: log.notification?.type ?? "default",
    message: log.notification?.message ?? "",
    message_hi: log.notification?.message_hi ?? "",
    created_by: log.notification?.created_by ?? "System",
    timestamp: log.notification?.created_at ?? log.created_at,
    is_read: !!log.is_read,
  });

  const normalizeFromWS = (n) => ({
    id: n.id ?? `${n.type}-${n.timestamp}-${n.message}`,
    source: "ws",
    type: n.type ?? "default",
    message: n.message ?? "",
    message_hi: n.message_hi ?? "",
    created_by: n.created_by ?? "System",
    timestamp: n.timestamp ?? new Date().toISOString(),
  });

  const notificationMessage = (n) =>
    locale?.startsWith("hi") && n.message_hi ? n.message_hi : n.message;

  const signature = useCallback((n) => {
    const type = (n?.type || "").toLowerCase();
    const msg = (n?.message || "").replace(/\s+/g, " ").trim().toLowerCase();
    return `${type}|${msg}`;
  }, []);

  const getNotificationTarget = useCallback((notification) => {
    const type = String(notification?.type || "").toLowerCase();
    const role = String(user?.role || "").toLowerCase();

    if (role === "admin") {
      return "/admin";
    }

    if (role === "manager") {
      if (["task_submission", "task_pending_approval"].includes(type)) {
        return "/manager";
      }
      if (["task_assignment", "task_approved", "task_declined", "deadline_reminder", "approval_delay"].includes(type)) {
        return "/manager/my-tasks";
      }
      return "/manager";
    }

    if (role === "team_member") {
      if (["task_assignment", "task_pending_approval", "task_approved", "task_declined", "deadline_reminder"].includes(type)) {
        return "/team-member/my-tasks";
      }
      return "/team-member";
    }

    return null;
  }, [user?.role]);

  const handleNotificationClick = useCallback((notification) => {
    const target = getNotificationTarget(notification);
    setMenuOpen(false);
    setUnreadApiSigsAtOpen(null);
    if (target) {
      navigate(target);
    }
  }, [getNotificationTarget, navigate]);

  // ——— API fetch (truth). Returns normalized items so caller can snapshot.
  const fetchListFromAPI = useCallback(async () => {
    try {
      if (isLocalDevSession && user?.email) {
        const items = getLocalDevNotifications(user.email).map(normalizeFromAPI);
        setApiList(items);
        return items;
      }

      const token = getStoredAccessToken();
      if (!token || !user?.email) return [];
      const res = await axios.get(`${API_URL}/api/notifications/`, {
        params: { email: user.email },
        headers: getAuthHeaders(token),
      });
      const items = (Array.isArray(res.data) ? res.data : []).map(normalizeFromAPI);
      setApiList(items);
      return items;
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
      return [];
    }
  }, [user?.email, isLocalDevSession]);

  // ——— Mark all API unread as read (server)
  const markAllUnreadAsRead = useCallback(async () => {
    try {
      if (isLocalDevSession) {
        if (user?.email) {
          markLocalDevNotificationsRead(user.email);
        }
        return;
      }
      const token = getStoredAccessToken();
      if (!token) return;
      await axios.patch(
        `${API_URL}/api/notifications/mark-read/`,
        {},
        { headers: getAuthHeaders(token) }
      );
    } catch (err) {
      console.error("Failed to mark notifications as read:", err);
    }
  }, [isLocalDevSession, user?.email]);
  
useEffect(() => {
  const onResize = () => setMenuOpen(false);
  window.addEventListener("resize", onResize);
  return () => window.removeEventListener("resize", onResize);
}, []);

  // ——— WebSocket
  useEffect(() => {
    if (!user || isLocalDevSession) return;
    const socket = connectWebSocket(user);
    if (!socket) return;

    const handleMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const normalized = normalizeFromWS(data);
        setWsQueue((prev) => [normalized, ...prev]);
      } catch (err) {
        console.error("Error parsing notification:", err);
      }
    };

    socket.addEventListener("message", handleMessage);
    return () => {
      socket.removeEventListener("message", handleMessage);
      disconnectWebSocket();
      setApiList([]);
      setWsQueue([]);
    };
  }, [user, isLocalDevSession]);

  // Initial fetch
  useEffect(() => {
    fetchListFromAPI();
  }, [fetchListFromAPI]);

  useEffect(() => {
    const handleRefresh = () => {
      fetchListFromAPI().then((items) => {
        if ((items || []).some((item) => !item.is_read)) {
          setActiveTab("unread");
        }
      });
    };
    window.addEventListener('notifications:refresh', handleRefresh);
    return () => window.removeEventListener('notifications:refresh', handleRefresh);
  }, [fetchListFromAPI]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
        setUnreadApiSigsAtOpen(null); // clear snapshot on close
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ——— Merge API + WS (API canonical when signature collides)
  const merged = useMemo(() => {
    const map = new Map(); // sig -> item

    const upsert = (src) => {
      const sig = signature(src);
      const prev = map.get(sig);

      if (!prev) {
        map.set(sig, { ...src });
        return;
      }

      // Prefer API as canonical; otherwise prefer newest by timestamp
      if (prev.source === "api" && src.source !== "api") return;
      if (src.source === "api" && prev.source !== "api") {
        map.set(sig, { ...src });
        return;
      }

      const newer = new Date(src.timestamp) > new Date(prev.timestamp) ? src : prev;
      map.set(sig, { ...newer });
    };

    (apiList || []).forEach(upsert);
    (wsQueue || []).forEach(upsert);

    return Array.from(map.values()).sort(
      (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
    );
  }, [apiList, wsQueue, signature]);

  // ——— API signatures for WS dedupe in unread calculation
  const apiSigs = useMemo(() => new Set((apiList || []).map(signature)), [apiList, signature]);

  // ——— Derive read/unread flags (handles fresh-login + WS-first-open)
  const mergedWithReadFlag = useMemo(() => {
    // While the menu is open we classify WS using the FROZEN previous-open time
    // so brand-new WS items appear in Unread on that first open.
    const rawCutoff = menuOpen ? highlightCutoff : lastOpenAt;
    const cutoff = rawCutoff ? new Date(rawCutoff).getTime() : 0;

    return (merged || []).map((n) => {
      if (n.source === "api") {
        if (menuOpen && unreadApiSigsAtOpen) {
          // During this open, treat items captured in snapshot as unread (fresh-login case)
          const wasUnreadAtOpen = unreadApiSigsAtOpen.has(signature(n));
          return { ...n, _unread: wasUnreadAtOpen };
        }
        // Otherwise rely on DB flag
        return { ...n, _unread: !n.is_read };
      }

      // WS: unread if newer than cutoff and not already represented by API
      const t = new Date(n.timestamp).getTime();
      const isUnreadWS = t > cutoff && !apiSigs.has(signature(n));
      return { ...n, _unread: isUnreadWS };
    });
  }, [merged, apiSigs, signature, menuOpen, highlightCutoff, lastOpenAt, unreadApiSigsAtOpen]);

  const unreadItems = useMemo(
    () => mergedWithReadFlag.filter((n) => n._unread),
    [mergedWithReadFlag]
  );
  const readItems = useMemo(
    () => mergedWithReadFlag.filter((n) => !n._unread),
    [mergedWithReadFlag]
  );

  // Derived API unread for badge
  const apiUnread = useMemo(
    () => (apiList || []).filter((n) => !n.is_read).length,
    [apiList]
  );

  // Badge = API unread + WS unseen since lastOpenAt (not already in API)
  const badgeCount = useMemo(() => {
    const cutoff = lastOpenAt ? new Date(lastOpenAt).getTime() : 0;
    const wsUnseen = (wsQueue || []).filter((n) => {
      const t = new Date(n.timestamp).getTime();
      return t > cutoff && !apiSigs.has(signature(n));
    }).length;
    return apiUnread + wsUnseen;
  }, [apiUnread, wsQueue, lastOpenAt, apiSigs, signature]);

  // Highlight/bold helper (first-open highlight)
  const isNewSinceLastOpen = (ts) => {
    if (!highlightCutoff) return false;
    try {
      return new Date(ts).getTime() > new Date(highlightCutoff).getTime();
    } catch {
      return false;
    }
  };

  // Open/close menu
  const toggleMenu = async () => {
    if (!menuOpen) {
      // Freeze the "previous open" time for this render; used for unread + highlight
      setHighlightCutoff(lastOpenAt ?? null);

      // 1) Fetch fresh API list and snapshot which were UNREAD at open
      const fresh = await fetchListFromAPI();
      const hasFreshUnread = (fresh || []).some((n) => !n.is_read);
      setActiveTab(hasFreshUnread || badgeCount > 0 ? "unread" : "read");
      const snapshotSigs = new Set(
        (fresh || [])
          .filter((n) => !n.is_read)
          .map((n) => signature(n))
      );
      setUnreadApiSigsAtOpen(snapshotSigs);

      // 2) Now mark-all-read on server (desired behavior)
      await markAllUnreadAsRead();

      // 3) Optimistically flip local API items so future opens show them as read
      setApiList((prev) => prev.map((x) => ({ ...x, is_read: true })));

      // Advance lastOpenAt to now (next openings use this as the baseline)
      const nowISO = new Date().toISOString();
      setLastOpenAt(nowISO);
      localStorage.setItem(LAST_OPEN_KEY, nowISO);
    } else {
      setUnreadApiSigsAtOpen(null); // closing: clear snapshot
    }
    setMenuOpen((prev) => !prev);
  };

  const listForTab = activeTab === "unread" ? unreadItems : readItems;

  return (
    <div className="relative inline-block" ref={menuRef}>
      <FaBell
        className="cursor-pointer text-text transition duration-200 hover:text-hover"
        size={20}
        onClick={toggleMenu}
        aria-label={t('common.notifications')}
      />
      {badgeCount > 0 && (
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full px-1">
          {badgeCount}
        </span>
      )}

      {menuOpen && (
        <div
  className="
    fixed inset-x-2 top-12
    md:inset-auto md:absolute md:right-0 md:mt-2
    w-[calc(100vw-1rem)] md:w-96
    dialog-surface z-[9999] overflow-hidden
  "
>
          {/* Tabs */}
          <div className="flex border-b border-border bg-secondary/20">
            <button
              className={`flex-1 py-3 text-sm ${activeTab === "unread" ? "border-b-2 border-purple-500 font-semibold text-purple-600" : "text-secondary-text"
                }`}
              onClick={(e) => {
                e.stopPropagation();
                setActiveTab("unread");
              }}
            >
              {t('notifications.unread')} ({unreadItems.length})
            </button>
            <button
              className={`flex-1 py-3 text-sm ${activeTab === "read" ? "border-b-2 border-purple-500 font-semibold text-purple-600" : "text-secondary-text"
                }`}
              onClick={(e) => {
                e.stopPropagation();
                setActiveTab("read");
              }}
            >
              {t('notifications.read')} ({readItems.length})
            </button>
          </div>

          <div className="max-h-[70vh] md:max-h-64 overflow-y-auto">
            {listForTab.length === 0 ? (
              <div className="p-3 text-center text-sm text-secondary-text">{t('notifications.noNotifications')}</div>
            ) : (
              listForTab.map((n) => {
                // Highlight if it's unread (API unread or WS unseen) OR if it's new since last open
                const isHighlighted = n._unread || isNewSinceLastOpen(n.timestamp);

                return (
                  <button
                    type="button"
                    key={n.id ?? `${n.type}-${n.timestamp}-${n.message}`}
                    onClick={() => handleNotificationClick(n)}
                    className={
                      "flex w-full items-start border-b border-border p-3 text-left text-sm last:border-0 " +
                      (isHighlighted ? "bg-purple-300/10 hover:bg-purple-300/20" : "hover:bg-background")
                    }
                  >
                    {typeIcons[n.type] || typeIcons.default}
                    <div className="flex-1">
                      <p className={`text-text ${isHighlighted ? "font-semibold" : ""}`}>
                        {notificationMessage(n)}
                      </p>
                      <div className="mt-1 text-xs text-secondary-text">
                        {t('notifications.from')}: {n.created_by} {n.source === "ws" ? `(${t('notifications.live')})` : ""}
                      </div>
                      <div className="text-xs text-muted">
                        {formatDateTime24(n.timestamp, locale)}
                      </div>
                    </div>
                  </button>
                );

              })
            )}
          </div>

          <div className="p-2 border-t text-center">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setMenuOpen(false);
                setUnreadApiSigsAtOpen(null); // clear snapshot
                onAllNotificationsClick?.();
              }}
              className="text-sm text-blue-600 hover:text-hover"
            >
              {t('notifications.viewAll')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
